import { useRef, useEffect } from 'react';
import { Box, Typography, Container, Paper, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import { SEO } from '../../components';
import { sentinelContent } from '../../data/content/sentinel';
import ModernList from '../../components/ModernList';
import PageTitle from '../../components/common/PageTitle';
import CustomAccordion from '../../components/common/CustomAccordion';
import THEME from '../../constants/theme';

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.1 },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const Dot = ({ color }) => (
  <Box
    component="span"
    sx={{
      mt: '7px',
      width: 5,
      height: 5,
      borderRadius: '50%',
      bgcolor: color || THEME.COLORS.PRIMARY_PURPLE,
      flexShrink: 0,
      display: 'inline-block',
    }}
  />
);

const SentinelPage = ({ setRefs }) => {
  const sentinelRef = useRef(null);
  const aboutRef = useRef(null);
  const getStartedRef = useRef(null);
  const featuresRef = useRef(null);
  const subscriptionRef = useRef(null);
  const faqsRef = useRef(null);

  useEffect(() => {
    setRefs({
      sentinel: sentinelRef,
      'sentinel-about': aboutRef,
      'sentinel-get-started': getStartedRef,
      'sentinel-features': featuresRef,
      'sentinel-subscription': subscriptionRef,
      'sentinel-faqs': faqsRef,
    });
  }, [setRefs]);

  const { about, getStarted, features, subscription, faqs } = sentinelContent;

  return (
    <>
      <SEO
        title="Sentinel | Real-Time Monitoring, Risk Control and Business Management"
        description={sentinelContent.hero.description}
        keywords="sentinel, risk management, monitoring, algorithmic trading, MAM, CPA, business management"
        canonical="/sentinel"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Sentinel',
          url: 'https://sentinel-risk.co.uk',
        }}
      />

      <Box ref={sentinelRef} id="sentinel" sx={{ minHeight: '100vh', pt: 8, position: 'relative' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
          <PageTitle
            title={sentinelContent.hero.title}
            subtitle={sentinelContent.hero.subtitle}
          />

          <Typography variant="body1" sx={{ mb: 5, maxWidth: 900, lineHeight: 1.8 }}>
            {sentinelContent.hero.description}
          </Typography>

          {/* ── ABOUT ─────────────────────────────────────────────────── */}
          <Box ref={aboutRef} id="sentinel-about" sx={{ py: { xs: 4, md: 6 } }}>
            <motion.div {...fadeUp}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.78) 100%)',
                }}
              >
                <Typography variant="h3" sx={{ mb: 3, fontWeight: 800, color: THEME.COLORS.PRIMARY_PURPLE }}>
                  {about.title}
                </Typography>

                {/* 1.1 What is Sentinel */}
                <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700 }}>
                  What is Sentinel
                </Typography>
                {about.intro.map((p, i) => (
                  <Typography key={i} variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                    {p}
                  </Typography>
                ))}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
                  {about.capabilities.map((c, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Dot />
                      <Typography variant="body2" sx={{ lineHeight: 1.7 }}>{c}</Typography>
                    </Box>
                  ))}
                </Box>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>{about.activeControl}</Typography>

                <Box sx={{ height: '1px', bgcolor: 'rgba(0,0,0,0.07)', my: 3 }} />

                {/* 1.2 What Sentinel Does */}
                <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700 }}>
                  What Sentinel Does
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5, lineHeight: 1.8 }}>
                  Sentinel operates on three core pillars:
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {about.pillars.map((pillar, idx) => (
                    <Grid item xs={12} md={4} key={idx}>
                      <Paper
                        elevation={2}
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          height: '100%',
                          borderTop: `3px solid ${THEME.COLORS.PRIMARY_PURPLE}`,
                          transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                          '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 10px 28px rgba(0,0,0,0.1)' },
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, mb: 1.5, color: THEME.COLORS.PRIMARY_PURPLE }}
                        >
                          {pillar.title}
                        </Typography>
                        {pillar.items.map((item, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                            <Dot />
                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{item}</Typography>
                          </Box>
                        ))}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ height: '1px', bgcolor: 'rgba(0,0,0,0.07)', my: 3 }} />

                {/* 1.3 Where Most Efficient */}
                <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700 }}>
                  Where Sentinel is Most Efficient
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  {about.efficiency.intro}
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      {about.efficiency.useCasesTitle}
                    </Typography>
                    {about.efficiency.useCases.map((uc, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.6 }}>
                        <Dot />
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{uc}</Typography>
                      </Box>
                    ))}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      {about.efficiency.whenTitle}
                    </Typography>
                    {about.efficiency.when.map((w, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.6 }}>
                        <Dot />
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{w}</Typography>
                      </Box>
                    ))}
                  </Grid>
                </Grid>

                <Box sx={{ height: '1px', bgcolor: 'rgba(0,0,0,0.07)', my: 3 }} />

                {/* 1.4 Supported Technologies */}
                <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700 }}>
                  Supported Technologies (v2.00)
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
                  {about.technologies.intro}
                </Typography>
                {about.technologies.current.map((t, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                    <Dot color="#22c55e" />
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{t}</Typography>
                  </Box>
                ))}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1.5, mb: 0.75 }}>
                  {about.technologies.plannedTitle}
                </Typography>
                {about.technologies.planned.map((t, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                    <Dot color="#a3a3a3" />
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>{t}</Typography>
                  </Box>
                ))}
                <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary', fontStyle: 'italic' }}>
                  {about.technologies.note}
                </Typography>

                <Box sx={{ height: '1px', bgcolor: 'rgba(0,0,0,0.07)', my: 3 }} />

                {/* 1.5 Subscription Model */}
                <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700 }}>
                  Subscription Model
                </Typography>
                <Typography variant="body1" sx={{ mb: 2.5, lineHeight: 1.8 }}>
                  {about.subscriptionModel.intro}
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: 'rgba(169,64,255,0.04)',
                    border: '1px solid rgba(169,64,255,0.14)',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, mb: 1, color: THEME.COLORS.PRIMARY_PURPLE }}
                  >
                    {about.subscriptionModel.freeTitle}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                    {about.subscriptionModel.freeIntro}
                  </Typography>
                  {about.subscriptionModel.freeItems.map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                      <Dot />
                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{item}</Typography>
                    </Box>
                  ))}
                  <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary', fontStyle: 'italic' }}>
                    {about.subscriptionModel.freeNote}
                  </Typography>
                </Paper>
              </Paper>
            </motion.div>
          </Box>

          {/* ── GET STARTED ───────────────────────────────────────────── */}
          <Box ref={getStartedRef} id="sentinel-get-started" sx={{ py: { xs: 4, md: 6 } }}>
            <motion.div {...fadeUp}>
              <Typography variant="h3" sx={{ mb: 1.5, fontWeight: 800, color: THEME.COLORS.PRIMARY_PURPLE }}>
                {getStarted.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
                {getStarted.intro}
              </Typography>
              <ModernList
                items={getStarted.steps.map((step, idx) => ({
                  text: `${idx + 1}. ${step.title}`,
                  children: [step.description],
                }))}
                spacing={2}
              />
            </motion.div>
          </Box>

          {/* ── FEATURES ──────────────────────────────────────────────── */}
          <Box ref={featuresRef} id="sentinel-features" sx={{ py: { xs: 4, md: 6 } }}>
            <motion.div {...fadeUp}>
              <Typography variant="h3" sx={{ mb: 2, fontWeight: 800, color: THEME.COLORS.PRIMARY_PURPLE }}>
                {features.title}
              </Typography>
              {features.intro && (
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
                  {features.intro}
                </Typography>
              )}
              <ModernList
                items={features.items.map(it => ({ text: it.title, children: [it.description] }))}
                spacing={2}
              />
            </motion.div>
          </Box>

          {/* ── SUBSCRIPTION ──────────────────────────────────────────── */}
          <Box ref={subscriptionRef} id="sentinel-subscription" sx={{ py: { xs: 4, md: 6 } }}>
            <motion.div {...fadeUp}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.78) 100%)',
                }}
              >
                <Typography variant="h3" sx={{ mb: 3, fontWeight: 800, color: THEME.COLORS.PRIMARY_PURPLE }}>
                  {subscription.title}
                </Typography>
                <CustomAccordion
                  items={subscription.items.map(it => ({ title: it.title, content: it.content }))}
                />
              </Paper>
            </motion.div>
          </Box>

          {/* ── FAQs ──────────────────────────────────────────────────── */}
          <Box ref={faqsRef} id="sentinel-faqs" sx={{ py: { xs: 4, md: 6 } }}>
            <motion.div {...fadeUp}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.78) 100%)',
                }}
              >
                <Typography variant="h3" sx={{ mb: 3, fontWeight: 800, color: THEME.COLORS.PRIMARY_PURPLE }}>
                  {faqs.title}
                </Typography>
                <CustomAccordion
                  items={faqs.items.map(it => ({ title: it.title, content: it.content }))}
                />
              </Paper>
            </motion.div>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default SentinelPage;
